import React from "react";
import { Table } from "react-bootstrap";
import { Link, NavLink } from "react-router-dom";

import { TEAM_LOGO_URL } from "../../lib/globals";
import { ITeam, ITournament } from "../../types/api";

interface TournamentStandingsProps {
    expanded?: boolean;
    data: ITournament;
    bordered: boolean;
    showLinks?: boolean;
    isMulti?: boolean;
    showHeader?: boolean;
}

const TournamentStandings = ({
    expanded = false,
    data,
    bordered,
    showLinks = false,
    isMulti = false,
    showHeader = false,
}: TournamentStandingsProps): React.JSX.Element => {
    const buildHeader = (tournament: ITournament) => {
        return tournament.standings ? (
            expanded ? (
                <thead>
                    {showHeader ? (
                        <tr>
                            <th colSpan={9}>
                                <h3>{tournament.ageLevel}</h3>
                            </th>
                        </tr>
                    ) : (
                        ""
                    )}
                    <tr>
                        <th className="standing">#</th>
                        <th className="logo"></th>
                        <th className="team">Team</th>
                        <th className="wins">W</th>
                        <th className="losses">L</th>
                        <th className="ties">Tie</th>
                        <th className="raavg">RA Avg</th>
                        <th className="rdavg">RD Avg</th>
                        <th className="rsavg">RS Avg</th>
                    </tr>
                </thead>
            ) : (
                <thead>
                    <tr>
                        <th colSpan={5}>
                            <h3>
                                <Link to={`/tournaments/${data.id}`}>
                                    {`${data.ageLevel} ${data.designation}`}
                                </Link>
                            </h3>
                        </th>
                    </tr>
                </thead>
            )
        ) : (
            <></>
        );
    };

    const buildBody = (tournament: ITournament) => {
        const standings = tournament.standings;

        const sortedTeams = standings
            ? tournament.teams?.slice().sort((a, b) => {
                  return tournament.standings
                      ? tournament.standings[a.id as ITeam["id"]].standing -
                            tournament.standings[b.id as ITeam["id"]].standing
                      : 0;
              })
            : [];

        return standings ? (
            expanded ? (
                <tbody>
                    {sortedTeams?.map((t, idx) => {
                        const teamId = t.id as ITeam["id"];

                        return (
                            <tr key={`tr-${idx}`}>
                                <td className="standing">
                                    {standings[teamId].standing}
                                </td>
                                <td className="logo">
                                    {t.logo && (
                                        <img
                                            src={`${TEAM_LOGO_URL}${t.logo}`}
                                            alt={`${t.name} logo`}
                                            className="teamLogo"
                                        />
                                    )}
                                </td>
                                <td className="team">
                                    <Link
                                        to={`/tournaments/${tournament.id}/teams/${t.id}`}
                                    >
                                        {t.name}
                                    </Link>
                                </td>
                                <td className="wins">
                                    {standings[teamId].wins}
                                </td>
                                <td className="losses">
                                    {standings[teamId].losses}
                                </td>
                                <td className="ties">
                                    {standings[teamId].ties}
                                </td>
                                <td className="raavg">
                                    {standings[teamId].raavg}
                                </td>
                                <td className="rdavg">
                                    {standings[teamId].rdavg}
                                </td>
                                <td className="rsavg">
                                    {standings[teamId].rsavg}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            ) : (
                <tbody>
                    <tr>
                        <td colSpan={2}></td>
                        <td className="wins">W</td>
                        <td className="losses">L</td>
                        <td className="ties">T</td>
                    </tr>
                    {sortedTeams?.map((t, idx) => {
                        const teamId = t.id as ITeam["id"];
                        return (
                            <tr key={`tr-${idx}`}>
                                <td className="logo">
                                    {t.logo && (
                                        <img
                                            src={`${TEAM_LOGO_URL}${t.logo}`}
                                            alt={`${t.name} logo`}
                                            className="teamLogo"
                                        />
                                    )}
                                </td>
                                <td className="team">
                                    <Link
                                        to={`/tournaments/${tournament.id}/teams/${t.id}`}
                                    >
                                        {t.name}
                                    </Link>
                                </td>
                                <td className="wins">
                                    {standings[teamId].wins}
                                </td>
                                <td className="losses">
                                    {standings[teamId].losses}
                                </td>
                                <td className="ties">
                                    {standings[teamId].ties}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            )
        ) : (
            <></>
        );
    };

    const buildFooter = () => {
        return (
            showLinks && (
                <tfoot>
                    <tr>
                        <td colSpan={5}>
                            <div>
                                <Link to={`/tournaments/${data.id}`}>
                                    Full Tournament Standings
                                </Link>

                                <Link to={`/tournaments/${data.id}/schedule`}>
                                    Tournament Schedule
                                </Link>

                                <Link to={`/tournaments/${data.id}/bracket`}>
                                    Bracket
                                </Link>
                            </div>
                        </td>
                    </tr>
                </tfoot>
            )
        );
    };

    return (
        <Table
            className={`tournamentStandings ${expanded ? "expanded" : ""} ${isMulti ? "multiDisplay" : ""} printSmaller`}
            striped
            bordered={bordered}
            size="sm"
            responsive
        >
            {buildHeader(data)}
            {buildBody(data)}
            {buildFooter()}
        </Table>
    );
};

export default TournamentStandings;
